/**
 * Standard procedures mapping for all workspaces
 * These are the default procedures available for each workspace
 */

import {
  Settings,
  AlertCircle,
  Users,
  TrendingUp,
  Calculator,
  DollarSign,
  FileText,
  Gavel,
  FileCheck,
  Shield,
  Building,
  BarChart3,
  LineChart,
  Building2,
  CreditCard,
  ClipboardList,
  AlertTriangle,
  Mail,
  Layers,
  Boxes,
  Heart,
  CheckCircle,
  FileCode,
  ClipboardCheck,
  Share2,
  LayoutDashboard,
} from "lucide-react";

export interface StandardProcedure {
  name: string;
  id: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const FRF_IND_AS = "Indian Accounting Standards (Ind AS)";
export const FRF_AS = "Accounting Standards (AS)";

export const STANDARD_PROCEDURES: Record<string, StandardProcedure[]> = {
  "Employee Benefits Expense": [
    { name: "IPE Testing", id: "ipe_testing", icon: Settings },
    { name: "Exception Testing", id: "exception_testing", icon: AlertCircle },
    { name: "Headcount Reconciliation", id: "headcount_reconciliation", icon: Users },
    { name: "M-o-M Analysis", id: "mom_analysis", icon: TrendingUp },
    { name: "Increment Analysis", id: "increment_analysis", icon: Calculator },
    { name: "Substantive Analytical Procedures", id: "salary_analytical", icon: DollarSign },
    { name: "Actuary Testing", id: "actuary_testing", icon: FileText },
    { name: "Managerial Remuneration", id: "managerial_remuneration", icon: Gavel },
    { name: "Share Based Payment", id: "share_based_payment", icon: Share2 },
  ],
  "Property, Plant & Equipment": [
    { name: "IPE Testing", id: "ipe-testing", icon: FileCheck },
    { name: "Exception Testing", id: "exception-testing", icon: Shield },
    { name: "Additions & Deletions Analysis", id: "additions-deletions", icon: TrendingUp },
    { name: "CWIP Analysis", id: "cwip-analysis", icon: Building },
    { name: "Non-Current Assets Held for Sale", id: "non-current-assets-held-for-sale", icon: ClipboardList },
  ],
  "Intangible Assets": [
    { name: "IPE Testing", id: "ipe-testing", icon: FileCheck },
    { name: "Exception Testing", id: "exception-testing", icon: AlertCircle },
    { name: "Additions & Deletions Listing", id: "additions-deletions", icon: TrendingUp },
    { name: "Intangible Assets Under Development", id: "under-development", icon: FileCode },
  ],
  "Lease Liability": [
    { name: "Lease Assessment", id: "lease-assessment", icon: FileCheck },
    { name: "Finance Lease", id: "finance-lease", icon: Building2 },
    { name: "Operating Lease", id: "operating-lease", icon: Building2 },
  ],
  "Lease Liability - Ind AS": [
    { name: "Lease Assessment", id: "lease-assessment", icon: FileCheck },
    { name: "Lease Testing", id: "lease-testing", icon: Building2 },
    { name: "Lease Reassessment", id: "lease-reassessment", icon: CreditCard },
    { name: "Lease Modification", id: "lease-modification", icon: FileText },
    { name: "Lease Accounting Exception", id: "lease-accounting-exception", icon: AlertCircle },
    { name: "Lease Disclosure", id: "lease-disclosure", icon: ClipboardCheck },
  ],
  "Right-of-Use Assets": [
    { name: "ROU Disclosure", id: "disclosure", icon: ClipboardCheck },
  ],
  "Cost of Materials Consumed": [
    { name: "IPE Testing", id: "ipe-testing", icon: FileCheck },
    { name: "Exception Testing", id: "exception-testing", icon: AlertCircle },
    { name: "Test of Details", id: "test-of-details", icon: ClipboardList },
    { name: "Analytical Procedures", id: "analytical-procedures", icon: BarChart3 },
    { name: "Predictive Analytics", id: "predictive-analytics", icon: LineChart },
    { name: "Purchase Analytical Dashboard", id: "purchase-analytical-dashboard", icon: LayoutDashboard },
  ],
  "Trade Payables": [
    { name: "IPE Testing", id: "ipe-testing", icon: FileText },
    { name: "Exception Testing", id: "exception-testing", icon: AlertTriangle },
    { name: "Ratio Analysis", id: "ratio-analysis", icon: Calculator },
    { name: "Confirmations", id: "confirmations", icon: Mail },
  ],
  "Inventory": [
    { name: "IPE Testing", id: "ipe-testing", icon: FileCheck },
    { name: "Exception Testing", id: "exception-testing", icon: Shield },
    { name: "NRV Assessment", id: "nrv-assessment", icon: BarChart3 },
    { name: "Cost Allocation", id: "cost-allocation", icon: Layers },
    { name: "Inventory Ageing", id: "inventory-ageing", icon: ClipboardList },
    { name: "Physical Verification", id: "physical-verification", icon: Boxes },
    { name: "Overhead Working", id: "overhead-working", icon: Layers },
  ],
  "Other Assets": [
    { name: "Prepaid Expense", id: "prepaid", icon: FileText },
    { name: "Security Deposit", id: "sec-deposit", icon: Shield },
    { name: "Advance to Suppliers - Completeness Check", id: "adv-suppliers-completeness", icon: CheckCircle },
    { name: "Capital Advances - Completeness Check", id: "capital-advances-completeness", icon: CheckCircle },
  ],
  "Other Financial Assets": [
    { name: "Security Deposits", id: "security-deposits", icon: Shield },
  ],
  "Trade Receivables": [
    { name: "IPE Testing", id: "ipe-testing", icon: FileCheck },
    { name: "Exception Testing", id: "exception-testing", icon: AlertCircle },
    { name: "Ratio Analysis", id: "ratio-analysis", icon: Calculator },
    { name: "ECL Flow Rate Analysis – YoY", id: "ecl-flow-rate-yoy", icon: LineChart },
    { name: "ECL Flow Rate Analysis – QoQ", id: "ecl-flow-rate-qoq", icon: LineChart },
  ],
  "Depreciation": [
    { name: "Depreciation on ROU", id: "depreciation-rou", icon: Building2 },
    { name: "Depreciation on Tangible Assets", id: "depreciation-tangible", icon: Building },
    { name: "Depreciation on Intangible Assets", id: "depreciation-intangible", icon: FileCode },
  ],
  "Other Expenses": [
    { name: "IPE Testing", id: "ipe-testing", icon: FileCheck },
    { name: "Analytical Procedures", id: "analytical-procedures", icon: BarChart3 },
    { name: "Corporate Social Responsibility", id: "corporate-social-responsibility", icon: Heart },
  ],
  "Borrowings": [
    { name: "Repayment testing", id: "repayment-testing", icon: CreditCard },
    { name: "Covenant testing", id: "covenant-testing", icon: Shield },
    { name: "Drawing Power", id: "drawing-power", icon: FileText },
    { name: "Agreement Summary", id: "agreement-summary", icon: ClipboardCheck },
    { name: "Quarterly Stock Statement", id: "quarterly-stock-statement", icon: BarChart3 },
    { name: "Classification testing", id: "classification-testing", icon: CheckCircle },
  ],
  "Borrowings - Ind AS": [
    { name: "Dashboard & Agreement Summary", id: "dashboard-agreement-summary", icon: ClipboardCheck },
    { name: "Repayment testing", id: "repayment-testing", icon: CreditCard },
    { name: "Covenant testing", id: "covenant-testing", icon: Shield },
    { name: "Drawing Power", id: "drawing-power", icon: FileText },
    { name: "Utilisation Testing", id: "utilisation-testing", icon: FileText },
    { name: "Quarterly Stock Statement", id: "quarterly-stock-statement", icon: BarChart3 },
    { name: "Classification testing", id: "classification-testing", icon: CheckCircle },
  ],
  "Cash & Cash Equivalents": [
    { name: "Bank Accounts", id: "bank-accounts", icon: Building2 },
  ],
  "Investments": [
    { name: "Investments Procedures", id: "investments-procedures", icon: Calculator },
  ],
  "Other Income": [
    { name: "Net Investment in Leases", id: "net-investment", icon: FileText },
    { name: "Other Procedures", id: "other-procedures", icon: FileText },
  ],
  "Revenue from Operations": [
    { name: "Ship and Bill - IPE Testing", id: "ship-bill-ipe-testing", icon: FileCheck },
    { name: "Cost Plus - Computation", id: "cost-plus-computation", icon: Calculator },
  ],
  "Other Liabilities": [
    { name: "Advance from customer - Completeness Check", id: "adv-cust-completeness", icon: CheckCircle },
    { name: "Advance from customer - Ageing Analysis", id: "adv-cust-ageing", icon: BarChart3 },
    { name: "Capex Vendors - Completeness Check", id: "capex-vendors-completeness", icon: CheckCircle },
    { name: "Capex Vendors - Ageing Analysis", id: "capex-vendors-ageing", icon: BarChart3 },
  ],
  "Other Financial Liabilities": [
    { name: "Capex Vendors - Completeness Check", id: "capex-vendors-completeness", icon: CheckCircle },
  ],
  "Other Receivables": [
    { name: "Advance to Suppliers - Completeness Check", id: "adv-suppliers-completeness", icon: CheckCircle },
    { name: "Advance to Suppliers - Ageing Analysis", id: "adv-suppliers-ageing", icon: BarChart3 },
    { name: "Capital Advances - Completeness Check", id: "capital-advances-completeness", icon: CheckCircle },
    { name: "Capital Advances - Ageing Analysis", id: "capital-advances-ageing", icon: BarChart3 },
  ],
  "Provisions & Contingent Liabilities": [
    { name: "Other Provisions", id: "other-provisions", icon: FileText },
  ],
  "Going Concern": [
    { name: "Going Concern Procedures", id: "going-concern", icon: Calculator },
    { name: "Assessment", id: "going-concern", icon: Calculator },
  ],
  "Litigations & Claims": [
    { name: "Litigations & Claims Procedures", id: "litigations-claims", icon: Calculator },
    { name: "Assessment", id: "assessment", icon: Calculator },
  ],
  "Opening Balance Testing": [
    { name: "Inquiry with Predecessor Auditor", id: "inquiry-predecessor", icon: FileText },
    { name: "SA 510 Assessment", id: "sa510-assessment", icon: ClipboardCheck },
  ],
  "Related Party Disclosure": [
    { name: "Compliance", id: "compliance", icon: Shield },
    { name: "Arms-Length Assessment", id: "arms-length", icon: FileCheck },
  ],
  "Management Use of Expert": [
    { name: "Assessment", id: "assessment", icon: Calculator },
  ],
  "Use of Auditor's Specialist": [
    { name: "Use of Auditor's Specialist Procedures", id: "use-of-auditors-specialist", icon: Calculator },
  ],
  "Compliance with Laws and Regulations": [
    { name: "Compliance Procedures", id: "compliance", icon: Calculator },
  ],
  "Review of Estimates for Management Bias": [
    { name: "Review of Estimates Procedures", id: "review-of-estimates", icon: Calculator },
  ],
  "Internal Audit": [
    { name: "Internal Audit Procedures", id: "internal-audit", icon: Calculator },
  ],
  "Biological Assets other than Bearer Plants": [
    { name: "IPE Testing", id: "ipe-testing", icon: Settings },
    { name: "Valuation Testing", id: "valuation-testing", icon: Calculator },
    { name: "Physical Verification", id: "physical-verification", icon: Boxes },
    { name: "Disclosure Testing", id: "disclosure-testing", icon: FileText },
  ],
};

/**
 * Get standard procedures for a workspace
 */
export function getStandardProceduresForWorkspace(
  workspaceName: string,
  frf?: string | null,
): StandardProcedure[] {
  const procedures = STANDARD_PROCEDURES[workspaceName] || [];
  if (!frf) return procedures;

  const isIndAS = frf === FRF_IND_AS;
  const isAS = frf === FRF_AS;

  if (workspaceName === "Employee Benefits Expense") {
    return procedures.filter(
      (proc) => proc.id !== "share_based_payment" || isIndAS,
    );
  }

  if (workspaceName === "Property, Plant & Equipment") {
    return procedures.filter(
      (proc) => proc.id !== "non-current-assets-held-for-sale" || isIndAS,
    );
  }

  if (workspaceName === "Trade Receivables") {
    return procedures.filter(
      (proc) =>
        !["ecl-flow-rate-yoy", "ecl-flow-rate-qoq"].includes(proc.id) ||
        isIndAS,
    );
  }

  if (workspaceName === "Other Liabilities") {
    return procedures.filter(
      (proc) =>
        !["capex-vendors-completeness", "capex-vendors-ageing"].includes(
          proc.id,
        ) || isAS,
    );
  }

  // Ind AS: advance/capital completeness substantive procedures sit under Other Assets; AS keeps them under Other Receivables
  if (workspaceName === "Other Assets") {
    return procedures.filter(
      (proc) =>
        !["adv-suppliers-completeness", "capital-advances-completeness"].includes(
          proc.id,
        ) || isIndAS,
    );
  }

  if (workspaceName === "Other Receivables") {
    return procedures.filter(
      (proc) =>
        !["adv-suppliers-completeness", "capital-advances-completeness"].includes(
          proc.id,
        ) || !isIndAS,
    );
  }

  return procedures;
}

